import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { apiClient } from "@copro/api";
import { useSpace } from "../../../hooks/useSpace";
import { pageRange } from "../../../utils/pagination";
import { toSearchWords } from "../../../utils/searchText";
import { keys } from "./keys";
import { ProviderTag, ProviderWithDetails } from "../types";

type Params = {
  search: string;
  /** 0-based. */
  page: number;
  pageSize: number;
  tagIds?: string[];
  underContractOnly?: boolean;
};

async function fetchProvidersPaged(
  spaceId: string,
  params: Params
): Promise<{ rows: ProviderWithDetails[]; total: number }> {
  // If filtering by tags, find the provider IDs assigned to those tags (matching any selected tag)
  let filterProviderIds: string[] | null = null;

  if (params.tagIds && params.tagIds.length > 0) {
    const { data: assignments, error: tagError } = await apiClient
      .from('provider_tag_assignments')
      .select('provider_id')
      .in('tag_id', params.tagIds);

    if (tagError) {
      throw tagError;
    }

    const tagProviderIds = Array.from(
      new Set((assignments ?? []).map((a) => a.provider_id))
    );
    if (tagProviderIds.length === 0) {
      return { rows: [], total: 0 };
    }
    filterProviderIds = tagProviderIds;
  }

  const { from, to } = pageRange(params.page, params.pageSize);

  let query = apiClient
    .from('providers_view')
    .select(
      '*, provider_tag_assignments(provider_tags(*))',
      { count: 'exact' }
    )
    .eq('space_id', spaceId)
    .order('name', { ascending: true })
    // Unique tie-breaker: providers sharing a name must not straddle two pages.
    .order('id', { ascending: true })
    .range(from, to);

  if (params.underContractOnly) {
    query = query.eq('has_active_contract', true);
  }

  if (filterProviderIds) {
    query = query.in('id', filterProviderIds);
  }

  // `search_text` = name, activity and address, lowercase without accents.
  for (const word of toSearchWords(params.search)) {
    query = query.ilike('search_text', `%${word}%`);
  }

  const { data, error, count } = await query;

  if (error) {
    throw error;
  }

  const rows: ProviderWithDetails[] = (data ?? []).map((row) => {
    const rawTags = (row.provider_tag_assignments ?? [])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((assignment: any) => assignment.provider_tags)
      .filter((tag: unknown): tag is ProviderTag => Boolean(tag));

    const tags = rawTags.sort((a: ProviderTag, b: ProviderTag) =>
      a.name.localeCompare(b.name)
    );

    return {
      id: row.id!,
      name: row.name!,
      description: row.description,
      address: row.address,
      user_id: row.user_id!,
      space_id: row.space_id!,
      created_at: row.created_at!,
      search_text: row.search_text,
      tags,
      hasActiveContract: Boolean(row.has_active_contract),
    };
  });

  return { rows, total: count ?? 0 };
}

export function useGetProvidersPaged(params: Params) {
  const { currentSpace } = useSpace();
  const normalizedParams = {
    ...params,
    tagIds: params.tagIds ? [...params.tagIds].sort() : undefined,
  };

  return useQuery({
    queryKey: keys.getProvidersPaged(currentSpace?.id ?? '', normalizedParams),
    queryFn: () => fetchProvidersPaged(currentSpace!.id, normalizedParams),
    enabled: !!currentSpace?.id,
    placeholderData: keepPreviousData,
  });
}
